/**
 * Canonical run settings for view transforms.
 * Run settings are the run identity (idempotency) and part of the DynamoDB PK.
 * Exactly five snake_case keys, alphabetical order, no whitespace in the stringified JSON.
 * No camelCase or other key names; validation is strict.
 */

/** Allowed keys for run settings, in alphabetical order (canonical serialization order). */
export const RUN_SETTINGS_KEYS = [
  "input_id",
  "organization_id",
  "run_attempt_id",
  "settings_name_and_version",
  "workflow_id",
] as const;

const RUN_SETTINGS_KEYS_SET = new Set<string>(RUN_SETTINGS_KEYS);

/**
 * Parse runSettings JSON string, validate it has exactly the five snake_case keys,
 * and return the canonical string (alphabetical keys, no whitespace).
 * Throws on invalid JSON or wrong keys. No camelCase conversion.
 */
export function toCanonicalRunSettingsJson(runSettingsStr: string): string {
  let parsed: unknown;
  try {
    parsed = JSON.parse(runSettingsStr);
  } catch {
    throw new Error(
      `Invalid runSettings: not valid JSON. Expected object with keys: ${RUN_SETTINGS_KEYS.join(", ")}`
    );
  }
  if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Error(
      `Invalid runSettings: must be an object with keys: ${RUN_SETTINGS_KEYS.join(", ")}`
    );
  }
  const obj = parsed as Record<string, unknown>;
  const keys = Object.keys(obj);
  const keySet = new Set(keys);
  for (const k of keys) {
    if (!RUN_SETTINGS_KEYS_SET.has(k)) {
      throw new Error(
        `Invalid runSettings: unexpected key "${k}". Expected exactly: ${RUN_SETTINGS_KEYS.join(", ")}`
      );
    }
  }
  for (const k of RUN_SETTINGS_KEYS) {
    if (!keySet.has(k)) {
      throw new Error(
        `Invalid runSettings: missing key "${k}". Expected exactly: ${RUN_SETTINGS_KEYS.join(", ")}`
      );
    }
  }
  const ordered: Record<string, string> = {};
  for (const k of RUN_SETTINGS_KEYS) {
    const v = obj[k];
    if (typeof v !== "string") {
      throw new Error(`Invalid runSettings: value for "${k}" must be a string`);
    }
    ordered[k] = v;
  }
  return JSON.stringify(ordered);
}

/**
 * Convert backend gateway RunSettings object (camelCase) to canonical JSON string.
 * Used when mapping getLastRun response to the Run type expected by RunProvider (runSettings as string).
 */
export function runSettingsObjectToCanonicalJson(rs: {
  inputId: string;
  organizationId: string;
  workflowId: string;
  settingsNameAndVersion: string;
  runAttemptId: string;
}): string {
  const ordered: Record<string, string> = {
    input_id: rs.inputId,
    organization_id: rs.organizationId,
    run_attempt_id: rs.runAttemptId,
    settings_name_and_version: rs.settingsNameAndVersion,
    workflow_id: rs.workflowId,
  };
  return JSON.stringify(ordered);
}
